import React, { FC, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import './TreeModeControl.scss';
import DataTable, { IDataTableColumn, IDataTableHandle } from '@srm/ui/data-table';
import { IDataTableSource, IServerLoadOptions } from '@srm/ui/data-table/server';
import { DictionariesService } from '@services/DictionariesService';
import { IDictionaryData } from '@models/dictionary/IDictionaryData';
import { IDictFilter } from '@models/Forms/IForms';
import { IDictSettings } from '@atoms/Dictpicker/IDictSettings';
import { dictBooleanFilter, filterResponseVoid } from '@utils/dictionaryFilters';
import { getLoadOptionsObject } from '@utils/loadOptionsConverter';
import { IDictpickerRefActions } from '@atoms/Dictpicker/IDictpickerRefActions';
import { ConfigService } from '@/configuration/services/configService';
import { validateLoadedData } from '@utils/helpersDictPicker';
import { compileScriptFunction } from '@utils/scriptEval';
import { useUserScripts } from '@hooks/useUserScripts';
import { sendNotification } from '@srm/ui/lib/notifications';
import { errorText, errorTraceId } from '@srm/core/errors';
import { buildDictColumns, buildDictSort, buildDictJoinedColumns } from './dictGridColumns';
import {
    parseDictionarySelectableLevels,
    isDictionaryLevelSelectable,
    withDictionarySelectionLevels,
    DictionarySelectionRow,
} from '../../dictionarySelectableLevels';
import { buildFlatTree, dictLevelFilter } from '@utils/dictionaryTree';

export interface ITreeModeControlProp extends IDictSettings {
    onSelectedRowItems: (value: IDictionaryData[]) => void;
    onConfirmRow?: (row: IDictionaryData) => void;
    onSelectedKeys: (value: string[]) => void;
    dictAttributes: string[];
    filterResponse: IDictFilter;
    maxStructLevel: number;
    controlRef: React.Ref<IDictpickerRefActions>;
}

/** Ширина дерева: сумма ширин колонок шаблона плюс место под чекбокс и шевроны. */
const EXTRA_WIDTH = 100;

/**
 * Скрипт-фильтр узла из конфигурации.
 *
 * Функции-помощники (`code`, `field`, `childrens`, `intersect`) — публичный
 * контракт шаблонов: переименовывать нельзя. Скрипт всегда выражение, поэтому
 * компилируется с явными параметрами, а не выполняется через eval в скоупе
 * компонента. Дети узла на момент проверки могут быть не загружены — `childrens`
 * отдаёт то, что уже пришло.
 */
const nodeMatchesScript = (row: IDictionaryData, children: IDictionaryData[] | undefined, script: string) => {
    const helpers = `
        function code (n = node){
            return n.data?.code;
        };

        function field (name, n = node) {
            return n.data?.fields.find((field) => field.name === name)?.value;
        };

        function childrens (n = node) {
            return n.children;
        };

        function intersect (array1, array2){
            return array1 && array2 && array1.some((item) => array2.includes(item));
        };
        `;
    const expression = script.trim().replace(/;+\s*$/, '');
    const node = { data: row, children: (children ?? []).map((child) => ({ data: child })) };
    return Boolean(compileScriptFunction(['node'], `${helpers}\nreturn (${expression});`)(node));
};

/**
 * Условие выборки по родителю для запроса `tree/items`.
 *
 * Родителя сервер берёт НЕ из поля `parentId` тела запроса (там оно значит лишь
 * «корневой ли это запрос» — от него зависят предикаты шаблона и подгрузка
 * родителей), а из условия фильтра по полю `parent`: пустое значение
 * разворачивается в предикат корней, id узла — в `dd_ParentData = id`. Без этого
 * условия эндпоинт на любой запрос отдаёт весь справочник плоско, игнорируя и
 * `parentId`, и `take` — отсюда «раскрытие первого уровня показывает весь
 * справочник» и минуты на больших справочниках (ОКАТО, ОКПД, ОКВЭД).
 *
 * Исключение — корневой запрос с активным фильтром строки: поиск обязан идти по
 * всему справочнику, а не по одним корням. Сервер вернёт найденные узлы вместе с
 * их родителями, и дерево соберётся на клиенте (см. clientTree в `loadRoots`).
 * Так же работал прежний грид, отправлявший это условие сам.
 */
const withParentCondition = (filter: unknown, parentId: string): unknown => {
    if (filter && !parentId) return filter;
    const parentCondition = dictLevelFilter(parentId);
    return filter ? [filter, 'and', parentCondition] : parentCondition;
};

/**
 * Иерархический справочник на базовом гриде.
 *
 * Дерево ленивое: корни приходят серверным источником таблицы, дети узла —
 * отдельным запросом при раскрытии (условие по `parent`, см. выше). Фильтры
 * строки уходят на сервер, а скрипт-фильтр шаблона отбирает уже загруженные
 * узлы на клиенте.
 */
const TreeModeControl: FC<ITreeModeControlProp> = (p: ITreeModeControlProp) => {
    const selectableRule = useMemo(() => parseDictionarySelectableLevels(p.selectableLevels), [p.selectableLevels]);
    const config = ConfigService.get();
    const applyFilterOnClick = config.application.applyGridFilterOnClick ?? false;
    const tableRef = useRef<IDataTableHandle<IDictionaryData>>(null);
    // Настройки пикера приходят новым объектом на каждый рендер модалки (spread
    // пропсов), а загрузчик и обработчики таблицы обязаны быть стабильными: новый
    // загрузчик — другая выборка (с начала, выбор снят). Читаем настройки через ref.
    const settingsRef = useRef(p);
    settingsRef.current = p;
    const [reloadToken, setReloadToken] = useState(0);
    /** Загруженные ветки: ключ узла → его дети. */
    const [childRows, setChildRows] = useState<Map<string, IDictionaryData[]>>(() => new Map());
    /** Те же ветки для загрузчика: он обязан не меняться при их пополнении. */
    const childRowsRef = useRef(childRows);
    childRowsRef.current = childRows;
    const [loadingRows, setLoadingRows] = useState<string[]>([]);
    const pendingChildrenRef = useRef(new Set<string>());
    /** Уровень узла (корни — 1): нужен ограничению `visibleLevels`. */
    const levelRef = useRef(new Map<string, number>());
    /** Родитель узла — цепочка предков для анти-цикловой фильтрации детей. */
    const parentsRef = useRef(new Map<string, string>());
    /** Клиентское дерево (id → дети), когда сервер отдал весь набор разом.
     *  Состояние (не ref): участвует в рендере строк — getSubRows/canExpandRow
     *  обязаны видеть его согласованно с пришедшими данными. */
    const [clientTree, setClientTree] = useState<Map<string, IDictionaryData[]> | null>(null);
    const clientTreeRef = useRef(clientTree);
    clientTreeRef.current = clientTree;
    /** Активный фильтр строки превращает ответ сервера в готовое дерево
     *  совпадений: лениво догружать в него неотфильтрованных детей нельзя. */
    const [serverFilterActive, setServerFilterActive] = useState(false);
    const initialSelectionRef = useRef(false);
    /** Узлы стартового выбора, ждущие отрисовки своих веток: таблица сообщает строки
     *  выбора из своей модели, и ключ ещё не отрисованной ветки ушёл бы без строки. */
    const [initialSelectedIds, setInitialSelectedIds] = useState<string[] | null>(null);
    /** Корни текущей выборки уже пришли. Стартовое раскрытие
     *  пути к выбранному обязано ждать её: иначе оно гонится с loadRoots — сброс
     *  веток стирает только что загруженный путь, а в full-set справочниках
     *  до-сбросовые expandRow ещё и сажают весь набор «детьми» каждого узла. */
    const rootsLoadedRef = useRef(false);
    const [rootsEpoch, setRootsEpoch] = useState(0);

    useImperativeHandle(p.controlRef, () => ({
        reset: () => {
            setReloadToken((token) => token + 1);
        },
        setSelected: (items: string[]) => {
            tableRef.current?.selectRows(items);
        },
        unSelect: (item: IDictionaryData) => {
            const { keys } = tableRef.current?.getSelected() ?? { keys: [] };
            tableRef.current?.selectRows(keys.filter((key) => key !== item.id));
        },
        clearSelection: () => {
            tableRef.current?.clearSelection();
        },
    }));

    // Смена справочника или обновление: стартовый выбор ставится заново.
    // Ветки/уровни/режим НЕ сбрасываются здесь: их атомарно переустанавливает
    // ответ корней в loadRoots — отдельный сброс-эффект жил бы в гонке с
    // асинхронной загрузкой (окно «дерево сброшено, данные ещё старые»).
    useEffect(() => {
        initialSelectionRef.current = false;
        rootsLoadedRef.current = false;
    }, [p.dictName, reloadToken]);

    /** Максимальный уровень раскрытия из настройки `visibleLevels`. */
    const maxVisibleLevel = useMemo(() => {
        if (!p.visibleLevels) return undefined;
        if (p.visibleLevels === 'first') return 1;
        const level = parseInt(p.visibleLevels);
        if (Number.isNaN(level) || level < 1) {
            console.error(`Некорректно указана настройка 'visibleLevels' (${p.visibleLevels})`);
            return undefined;
        }
        return level;
    }, [p.visibleLevels]);

    const isLastVisibleLevel = useCallback(
        (row: IDictionaryData) =>
            maxVisibleLevel !== undefined && (levelRef.current.get(row.id) ?? 1) >= maxVisibleLevel,
        [maxVisibleLevel],
    );

    /** Отбор шаблона: условие по кодам считает сервер, скрипт — клиент. */
    const applyTemplateFilters = useCallback((rows: IDictionaryData[]) => {
        const { filterResponse } = settingsRef.current;
        let result = filterResponse ? filterResponseVoid(rows, filterResponse) : rows;
        if (filterResponse?.script) {
            const script = filterResponse.script;
            result = result.filter((row) => nodeMatchesScript(row, childRowsRef.current.get(row.id), script));
        }
        return result;
    }, []);

    const fetchNodes = useCallback(
        async (options: IServerLoadOptions, parentId: string) => {
            const s = settingsRef.current;
            const body = {
                options: getLoadOptionsObject({
                    ...options,
                    // логические атрибуты справочник хранит числом — см. dictBooleanFilter
                    filter: dictBooleanFilter(withParentCondition(options.filter, parentId)),
                }),
                // parentId сервер принимает одной строкой — контракт эндпоинта
                parentId,
                formValues: await s.getFormValuesAsync(),
                predicates: s.predicatesCache || undefined,
                loadMode: s.loadMode || undefined,
            };
            const response = await DictionariesService.getTreeItems(s.dictName, body);
            validateLoadedData(response.data.data, s.loadedDataValidators);
            const rows = await withDictionarySelectionLevels(
                applyTemplateFilters(response.data.data ?? []),
                parseDictionarySelectableLevels(s.selectableLevels),
                s.isFormData,
                (codes) => DictionariesService.getTreeItemsByCode(s.dictName, codes),
            );
            // tree/items возвращает весь уровень (при поиске — совпадения с
            // предками), без skip/take. После фильтра шаблона это тоже полный
            // набор, поэтому счётчик относится к оставшимся узлам.
            return { rows, totalCount: rows.length };
        },
        [applyTemplateFilters],
    );

    // Обычно на запрос корней приходят только корни, и дети догружаются по
    // раскрытию. Но ответ бывает и полным набором разом: при `loadMode="all"`
    // (сервер сам обходит все уровни), при предикатах шаблона вместо предиката
    // корней и при поиске по фильтру (найденные узлы вместе с родителями).
    // Такой набор распознаём по не-корневым узлам в ответе и строим дерево на
    // клиенте — как на странице справочников; иначе второй запрос вернул бы те
    // же узлы и дерево вырождалось в дубликаты.
    const loadRoots = useCallback(
        async (options: IServerLoadOptions, signal: AbortSignal) => {
            const result = await fetchNodes(options, '');
            // Хук серверных данных отбросит устаревший ответ, а локальные ветки
            // дерева принадлежат компоненту — не даём отменённому запросу
            // перезаписать их самостоятельно.
            if (signal.aborted) return result;
            // Новый полный набор корней заменяет прежнюю выборку и её ветки.
            setChildRows(new Map());
            setLoadingRows([]);
            pendingChildrenRef.current.clear();
            levelRef.current.clear();
            parentsRef.current.clear();
            // корни пришли и обработаны — стартовому раскрытию пути можно ехать
            setServerFilterActive(!!options.filter);
            rootsLoadedRef.current = true;
            setRootsEpoch((epoch) => epoch + 1);
            const hasNonRoot = result.rows.some((row) => (row.parent ?? '') !== '');
            if (!hasNonRoot) {
                setClientTree(null);
                result.rows.forEach((row) => levelRef.current.set(row.id, 1));
                return result;
            }
            // при серверном фильтре ветки без найденного родителя всплывают корнями
            const { roots, children } = buildFlatTree(result.rows);
            levelRef.current.clear();
            const walk = (list: IDictionaryData[], level: number) => {
                list.forEach((row) => {
                    if (levelRef.current.has(row.id)) return;
                    levelRef.current.set(row.id, level);
                    walk(children.get(row.id) ?? [], level + 1);
                });
            };
            walk(roots, 1);
            setClientTree(children);
            return { rows: roots, totalCount: roots.length };
        },
        [fetchNodes],
    );

    const source = useMemo<IDataTableSource<IDictionaryData>>(
        () => ({ load: loadRoots, refreshToken: reloadToken }),
        [loadRoots, reloadToken],
    );

    const expandRow = useCallback(
        async (row: IDictionaryData) => {
            if (isLastVisibleLevel(row)) return;
            // клиентское дерево: дети уже в наборе, догружать нечего
            if (clientTreeRef.current) return;
            if (childRowsRef.current.has(row.id) || pendingChildrenRef.current.has(row.id)) return;
            pendingChildrenRef.current.add(row.id);
            setLoadingRows([...pendingChildrenRef.current]);
            try {
                const { rows: loaded } = await fetchNodes({ skip: 0, take: 0, requireTotalCount: true }, row.id);
                // Анти-цикл: узел не может быть собственным ребёнком или ребёнком
                // своего потомка — такие данные (кривой parent на бэке) уводили
                // построение дерева в бесконечную рекурсию и роняли модалку
                const ancestors = new Set<string>();
                for (let cur: string | undefined = row.id, i = 0; cur && i < 200; i += 1) {
                    ancestors.add(cur);
                    cur = parentsRef.current.get(cur);
                }
                const rows = loaded.filter((child) => !ancestors.has(child.id));
                const level = (levelRef.current.get(row.id) ?? 1) + 1;
                rows.forEach((child) => {
                    levelRef.current.set(child.id, level);
                    parentsRef.current.set(child.id, row.id);
                });
                setChildRows((prev) => new Map(prev).set(row.id, rows));
            } catch (error: unknown) {
                // ветка осталась свёрнутой: дерево цело, потеряно только раскрытие
                sendNotification({
                    message: `Вложенные элементы не загрузились. ${errorText(error, 'Повторите раскрытие.')}`,
                    traceId: errorTraceId(error),
                    variant: 'red',
                });
            } finally {
                pendingChildrenRef.current.delete(row.id);
                setLoadingRows([...pendingChildrenRef.current]);
            }
        },
        [fetchNodes, isLastVisibleLevel],
    );

    const getSubRows = useCallback(
        // Ограничение действует и на уже загруженные ветки поиска: одного
        // canExpandRow недостаточно, когда таблица получила готовые subRows.
        (row: IDictionaryData) =>
            isLastVisibleLevel(row) ? undefined : (clientTree?.get(row.id) ?? childRows.get(row.id)),
        [clientTree, childRows, isLastVisibleLevel],
    );

    // При поиске сервер возвращает совпадения вместе с предками: раскрываем все
    // построенные ветки, чтобы найденные листья сразу были видны. Новая обычная
    // выборка сворачивает старые id — иначе после сброса фильтра получался
    // «раскрытый», но пустой узел с ещё не загруженными детьми.
    // Повторная загрузка той же выборки (дребезг параметров запроса) приходит
    // новым Map — по идентичности она неотличима от новой выборки и схлопывала
    // ветки, раскрытые пользователем секунду назад. Сверяем состав.
    const expandSyncRef = useRef<string | null>(null);
    useEffect(() => {
        const keys = clientTree ? [...clientTree.keys()].join(',') : '';
        const signature = `${reloadToken}|${serverFilterActive}|${keys}`;
        if (expandSyncRef.current === signature) return;
        expandSyncRef.current = signature;
        tableRef.current?.setExpandedRows(serverFilterActive && clientTree ? [...clientTree.keys()] : []);
    }, [serverFilterActive, clientTree, reloadToken]);

    /** Ниже `visibleLevels` дерево не раскрывается. */
    const canExpandRow = useCallback(
        (row: IDictionaryData) => {
            if (isLastVisibleLevel(row)) return false;
            // в клиентском дереве раскрываемость определяют реально имеющиеся дети:
            // hasChild бэка может указывать на узлы, не попавшие в набор (фильтр)
            if (clientTree) {
                if (!clientTree.get(row.id)?.length) return false;
            } else if (serverFilterActive || !row.hasChild) {
                return false;
            }
            return true;
        },
        [clientTree, isLastVisibleLevel, serverFilterActive],
    );

    const columns = useMemo<IDataTableColumn<IDictionaryData>[]>(
        () => [
            ...buildDictColumns({
                dictAttributes: p.dictAttributes,
                gridAttribute: p.gridAttribute,
                isFormData: p.isFormData,
            }),
            ...buildDictJoinedColumns(p.gridAttribute),
        ],
        [p.dictAttributes, p.gridAttribute, p.isFormData],
    );

    const defaultSort = useMemo(
        () => buildDictSort({ dictAttributes: p.dictAttributes, gridAttribute: p.gridAttribute }),
        [p.dictAttributes, p.gridAttribute],
    );

    // Граница visibleLevels считается листом для выбора, как в прежнем гриде,
    // но исходный hasChild сервера сохраняется. Поиск может не вернуть детей
    // промежуточного узла — от этого он не становится доступным для выбора.
    const isVisibleLeaf = useCallback(
        (row: IDictionaryData) => isLastVisibleLevel(row) || (!row.hasChild && !clientTree?.get(row.id)?.length),
        [clientTree, isLastVisibleLevel],
    );

    /** Уровни справочника, доступные для выбора (`selectableLevels`). */
    const isRowSelectable = useCallback(
        (row: DictionarySelectionRow) =>
            isDictionaryLevelSelectable(
                selectableRule,
                row.selectionLevel,
                (levelRef.current.get(row.id) ?? 1) === 1,
                isVisibleLeaf(row),
            ),
        [isVisibleLeaf, selectableRule],
    );

    const recursive = !!p.isMultiple && (p.recursive ?? false);

    const onSelectionChange = useCallback(
        (keys: string[], rows: IDictionaryData[]) => {
            const s = settingsRef.current;
            // при рекурсивном выборе в поле уходят конечные элементы (аналог leavesOnly):
            // родительские узлы — только способ отметить ветку
            const restrictSelection = selectableRule.mode === 'range' || selectableRule.mode === 'invalid';
            const selectedRows = rows.filter(
                (row) => (!recursive || isVisibleLeaf(row)) && (!restrictSelection || isRowSelectable(row)),
            );
            // Ключ скрытого поиском узла остаётся ожидающим: его отсутствие в rows
            // не означает, что пользователь снял выбор или что уровень запрещён.
            const loadedKeys = new Set(rows.map((row) => row.id));
            const allowedKeys = new Set(selectedRows.map((row) => row.id));
            const selectedKeys = recursive
                ? [...allowedKeys]
                : restrictSelection
                  ? keys.filter((key) => !loadedKeys.has(key) || allowedKeys.has(key))
                  : keys;
            s.onSelectedKeys(selectedKeys);
            s.onSelectedRowItems(selectedRows);
        },
        [recursive, isVisibleLeaf, isRowSelectable, selectableRule],
    );

    // Стартовый выбор: коды поля превращаются в узлы, их путь раскрывается, а сами
    // узлы отмечаются — иначе выбранное значение не видно в свёрнутом дереве.
    // Ждём загрузку корней (rootsEpoch): раскрытие пути параллельно с
    // loadRoots попадало под его атомарный сброс веток.
    useEffect(() => {
        if (!rootsLoadedRef.current) return;
        if (initialSelectionRef.current) return;
        if (!p.selected || p.selected.length === 0) return;
        initialSelectionRef.current = true;
        let cancelled = false;
        DictionariesService.getTreeItemsByCode(p.dictName, p.selected)
            .then(async (response) => {
                const selected = response.data ?? [];
                for (const item of selected) {
                    const ids = item.fullId.split('/');
                    ids.pop();
                    for (const id of ids) {
                        if (cancelled) return;
                        // сначала дети узла, потом раскрытие: свёрнутую ветку
                        // таблице раскрывать нечем
                        await expandRow({ id, hasChild: true } as IDictionaryData);
                        tableRef.current?.expandRow(id, true);
                    }
                }
                if (cancelled) return;
                setInitialSelectedIds(selected.map((item) => item.id));
            })
            .catch(console.error);
        return () => {
            cancelled = true;
        };
        // раскрытие пути делается один раз на справочник (см. initialSelectionRef)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [p.dictName, p.selected, rootsEpoch]);

    // после коммита: загруженные ветки пути уже в модели таблицы
    useEffect(() => {
        if (!initialSelectedIds) return;
        tableRef.current?.selectRows(initialSelectedIds);
        setInitialSelectedIds(null);
    }, [initialSelectedIds]);

    const totalWidth = useMemo(
        () => EXTRA_WIDTH + (p.gridAttribute?.attrs.reduce((sum, attr) => sum + attr.width, 0) || 0),
        [p.gridAttribute?.attrs],
    );

    // Пользовательские скрипты строки и ячейки: контекст повторяет прежнее
    // событие грида — его формат ждут скрипты конфигураций
    const { onRowPreparedScript, onCellPreparedScript } = useUserScripts(
        p.userScripts?.onRowPrepared,
        p.userScripts?.onCellPrepared,
    );

    const onRowRendered = useCallback(
        ({ element, row, rowIndex }: { element: HTMLElement; row: IDictionaryData; rowIndex: number }) => {
            try {
                onRowPreparedScript?.({ rowElement: element, data: row, key: row.id, rowIndex, rowType: 'data' });
            } catch (error) {
                console.error('Скрипт строки справочника завершился ошибкой', error);
            }
        },
        [onRowPreparedScript],
    );

    const headerByField = useMemo(() => new Map(columns.map((column) => [column.field, column.header])), [columns]);

    const onCellRendered = useCallback(
        ({
            element,
            row,
            field,
            value,
            text,
        }: {
            element: HTMLElement;
            row: IDictionaryData;
            field: string;
            value: unknown;
            text: string;
        }) => {
            // значение ячейки в data-value: на него завязаны стили конфигураций
            if (!element.getAttribute('data-value')) element.setAttribute('data-value', text);
            try {
                onCellPreparedScript?.({
                    cellElement: element,
                    data: row,
                    key: row.id,
                    rowType: 'data',
                    column: { dataField: field, name: field, caption: headerByField.get(field) ?? field },
                    value,
                    text,
                    displayValue: text,
                });
            } catch (error) {
                console.error('Скрипт ячейки справочника завершился ошибкой', error);
            }
        },
        [onCellPreparedScript, headerByField],
    );

    return (
        <div className="dict-tree" style={{ width: totalWidth }}>
            <DataTable<IDictionaryData>
                controlRef={tableRef}
                columns={columns}
                data={[]}
                getRowId={(row) => row.id}
                source={source}
                getSubRows={getSubRows}
                canExpandRow={canExpandRow}
                onExpandRow={expandRow}
                loadingRowIds={loadingRows}
                defaultSort={defaultSort}
                // tree/items сортировку игнорирует (провайдер зовётся без sort):
                // порядок считаем на клиенте по загруженным узлам — так же делал
                // прежний грид (RemoteOperations sorting={false}). Иначе клик по
                // заголовку слал бы холостой запрос и сворачивал раскрытые ветки.
                serverSorting={false}
                // tree/items не поддерживает страницы: весь уровень приходит
                // сразу, дети — отдельными запросами expandRow по родителю.
                serverScrollMode="all"
                paging={false}
                selectable
                singleSelection={!p.isMultiple}
                selectOnRowClick
                isRowSelectable={isRowSelectable}
                recursiveSelection={recursive}
                onSelectionChange={onSelectionChange}
                onRowDoubleClick={p.isMultiple ? undefined : p.onConfirmRow}
                showFilterRow
                applyFiltersOnClick={applyFilterOnClick}
                defaultWrapMode={p.wordWrapEnabled ? 'wrap' : 'nowrap'}
                onRowRendered={onRowPreparedScript ? onRowRendered : undefined}
                onCellRendered={onCellRendered}
                fillHeight
                fitColumns
                overflowTooltip
                emptyText="Нет строк"
            />
        </div>
    );
};

export default TreeModeControl;
